import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import kingImg from "@/assets/species/king-salmon.png";
import silverImg from "@/assets/species/silver-salmon.png";
import halibutImg from "@/assets/species/halibut.png";
import lingcodImg from "@/assets/species/lingcod.png";
import rockfishImg from "@/assets/species/rockfish.png";
import pinkImg from "@/assets/species/pink-salmon.png";
import crabImg from "@/assets/species/crab-prawns.png";

export const Route = createFileRoute("/fish-species")({
  head: () =>
    seoHead({
      path: "/fish-species",
      title: "Fish Species — Coffman Cove Lodging",
      description:
        "The salmon, halibut, rockfish and lingcod you'll target out of Coffman Cove, Alaska — plus seasons, tackle notes and typical size.",
    }),
  component: FishSpecies,
});

const SPECIES = [
  {
    img: kingImg,
    name: "King Salmon (Chinook)",
    season: "May – July · peak June",
    size: "20 – 45 lbs · trophies 50+",
    length: "Avg length: 33 – 40 in · trophies 48\"+",
    notes:
      "Trolled cut-plug herring or flashers with hoochies behind downriggers. Early-morning bite off Point Baker & Warren Island.",
  },
  {
    img: silverImg,
    name: "Silver Salmon (Coho)",
    season: "Late July – September",
    size: "8 – 15 lbs · aggressive fighters",
    length: "Avg length: 24 – 30 in",
    notes:
      "Fast, acrobatic surface bite. Bucktails, spinners, or trolled herring at 30–60 ft. Terrific light-tackle target.",
  },
  {
    img: halibutImg,
    name: "Pacific Halibut",
    season: "May – September",
    size: "20 – 100 lbs typical · 150+ possible",
    length: "Avg length: 32 – 60 in · barn doors 72\"+",
    notes:
      "Bottom-fished with circle hooks, herring and salmon bellies. Best on soft tide changes over 200–350 ft gravel flats.",
  },
  {
    img: lingcodImg,
    name: "Lingcod",
    season: "Mid-May – November (check regs)",
    size: "10 – 40 lbs · toothy predators",
    length: "Avg length: 28 – 40 in · giants 50\"+",
    notes:
      "Jigged with heavy leadheads over rock pinnacles. Often follow up hooked rockfish — keep a rod rigged and ready.",
  },
  {
    img: rockfishImg,
    name: "Yelloweye & Black Rockfish",
    season: "Year-round (yelloweye limited)",
    size: "3 – 15 lbs",
    length: "Avg length: 16 – 26 in (yelloweye to 30\")",
    notes:
      "Structure fish over reefs and drop-offs. Descending devices required for yelloweye — we carry them on every boat.",
  },
  {
    img: pinkImg,
    name: "Pink & Chum Salmon",
    season: "July – August",
    size: "3 – 8 lbs (pinks) · 8 – 15 lbs (chums)",
    length: "Avg length: 20 – 24 in (pinks) · 24 – 32 in (chums)",
    notes:
      "Non-stop action on light gear near river mouths. Great for kids and first-time salmon anglers.",
  },
  {
    img: crabImg,
    name: "Dungeness Crab & Spot Prawns",
    season: "Summer (check regs & permits)",
    size: "Crab 6\"+ · prawns by the pound",
    length: "Avg carapace: 6.5 – 8 in (crab) · prawns 6 – 9 in",
    notes:
      "Pull pots between trolling passes. Bring them back to the dock for a fresh-off-the-boat dinner.",
  },
];

function FishSpecies() {
  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-20 pb-16">
          <p className="uppercase tracking-[0.3em] text-xs text-white/70">Fishing</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 max-w-3xl text-balance text-white">
            What you'll catch out of Coffman Cove.
          </h1>
          <p className="mt-5 max-w-2xl text-white/90">
            Southeast Alaska's Prince of Wales Island puts world-class salmon, halibut and bottom
            fish inside a short run of the dock. Here's what's biting and when.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="grid gap-6 md:grid-cols-2">
          {SPECIES.map((s) => (
            <article
              key={s.name}
              className="rounded-lg border border-border bg-card overflow-hidden flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[var(--color-parchment)] flex items-center justify-center p-4">
                <img
                  src={s.img}
                  alt={s.name}
                  loading="lazy"
                  width={1024}
                  height={768}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h2 className="font-display text-2xl">{s.name}</h2>
                <p className="text-[var(--color-ember)] font-semibold mt-1 text-sm">{s.season}</p>
                <dl className="mt-3 grid gap-1 text-sm">
                  <div className="flex gap-2">
                    <dt className="font-semibold text-foreground">Weight:</dt>
                    <dd className="text-muted-foreground">{s.size}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="font-semibold text-foreground">Length:</dt>
                    <dd className="text-muted-foreground">{s.length.replace(/^Avg (length|carapace): /, "")}</dd>
                  </div>
                </dl>
                <p className="mt-4 text-sm">{s.notes}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 rounded-xl bg-[var(--color-parchment)] border border-border p-8 text-center">
          <h2 className="font-display text-2xl text-white">Ready to chase them?</h2>
          <p className="mt-2 text-sm text-white/80 max-w-xl mx-auto">
            Check today's live conditions and tide-driven windows, or book your dates now.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link
              to="/fishing-report"
              className="inline-flex items-center rounded-md bg-[var(--color-ember)] text-[var(--color-ember-foreground)] px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:opacity-90"
            >
              Live fishing report →
            </Link>
            <a
              href="https://coffmancovelodging.com/contact"
              className="inline-flex items-center rounded-md border border-[var(--color-ember)] text-[var(--color-ember)] px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-[var(--color-ember)] hover:text-[var(--color-ember-foreground)]"
            >
              Book your trip →
            </a>
          </div>
        </div>

      </section>
    </div>
  );
}
